/**
 * Bank Withdrawal Management - Hook for managing withdrawal state and API calls
 */
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useToast } from '../../../hooks/use-toast';
import { useAuth } from '../../../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const useWithdrawalManagement = () => {
  const { toast } = useToast();
  const { user } = useAuth();
  
  const [withdrawals, setWithdrawals] = useState([]);
  const [bankLinks, setBankLinks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    approved: 0,
    completed: 0,
    cancelled: 0,
    total_amount: 0
  });
  const [filters, setFilters] = useState({
    status: 'all',
    search: '',
    dateRange: 'all'
  });

  const headers = {
    'Content-Type': 'application/json'
  };

  // Fetch withdrawals
  const fetchWithdrawals = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API_URL}/api/bank/dashboard/withdrawals`,
        { headers, withCredentials: true }
      );
      setWithdrawals(response.data.withdrawals || []);
      if (response.data.stats) {
        setStats(response.data.stats);
      }
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to fetch withdrawals'
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  // Bank links are not used in this simplified version
  const fetchBankLinks = useCallback(async () => {
    // Bank links fetch removed - not needed for admin dashboard
    setBankLinks([]);
  }, []);

  useEffect(() => {
    fetchWithdrawals();
    fetchBankLinks();
  }, [fetchWithdrawals, fetchBankLinks]);

  // Approve withdrawal
  const handleApprove = useCallback(async (withdrawalId) => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/bank/dashboard/withdrawal/approve`,
        { withdrawal_id: withdrawalId },
        { headers, withCredentials: true }
      );
      
      toast({
        title: 'Success',
        description: response.data.message
      });
      
      fetchWithdrawals();
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to approve withdrawal'
      });
    } finally {
      setLoading(false);
    }
  }, [fetchWithdrawals, toast]);

  // Cancel withdrawal
  const handleCancel = useCallback(async (withdrawalId, reason) => {
    if (!reason) return;

    setLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/bank/dashboard/withdrawal/cancel`,
        { withdrawal_id: withdrawalId, reason },
        { headers, withCredentials: true }
      );
      
      toast({
        title: 'Success',
        description: response.data.message
      });
      
      fetchWithdrawals();
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to cancel withdrawal'
      });
    } finally {
      setLoading(false);
    }
  }, [fetchWithdrawals, toast]);

  // Refund withdrawal
  const handleRefund = useCallback(async (withdrawalId, reason) => {
    if (!reason) return;

    setLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/bank/dashboard/withdrawal/refund`,
        { withdrawal_id: withdrawalId, reason },
        { headers, withCredentials: true }
      );
      
      toast({
        title: 'Success',
        description: response.data.message
      });
      
      fetchWithdrawals();
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to refund withdrawal'
      });
    } finally {
      setLoading(false);
    }
  }, [fetchWithdrawals, toast]);

  // Create withdrawal
  const handleCreate = useCallback(async (withdrawalData) => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/bank/dashboard/withdrawal/create`,
        {
          ...withdrawalData,
          amount: parseFloat(withdrawalData.amount)
        },
        { headers, withCredentials: true }
      );
      
      toast({
        title: 'Success',
        description: response.data.message
      });
      
      fetchWithdrawals();
      return true;
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to create withdrawal'
      });
      return false;
    } finally {
      setLoading(false);
    }
  }, [fetchWithdrawals, toast]);

  // Filter withdrawals
  const filteredWithdrawals = withdrawals.filter(w => {
    if (filters.status !== 'all' && w.status !== filters.status) return false;
    if (filters.search) {
      const search = filters.search.toLowerCase();
      if (!w.user_id?.toLowerCase().includes(search) &&
          !w.id?.toLowerCase().includes(search)) return false;
    }
    return true;
  });

  return {
    withdrawals: filteredWithdrawals,
    allWithdrawals: withdrawals,
    bankLinks,
    loading,
    stats,
    filters,
    setFilters,
    fetchWithdrawals,
    handleApprove,
    handleCancel,
    handleRefund,
    handleCreate
  };
};

export default useWithdrawalManagement;
